import { Link } from "react-router-dom";
import { Leaf, Instagram, Phone, Mail, MapPin } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { BUSINESS } from "@/data/site";
import { WhatsAppIcon } from "@/components/Icons";

export default function Footer() {
  const { t, lang } = useLanguage();
  const links = [
    { to: "/", label: t.nav.home, id: "footer-home-link" },
    { to: "/about", label: t.nav.about, id: "footer-about-link" },
    { to: "/our-tea", label: t.nav.ourTea, id: "footer-our-tea-link" },
    { to: "/track", label: t.footer.track, id: "footer-track-link" },
    { to: "/contact", label: t.nav.contact, id: "footer-contact-link" },
  ];
  const legal = [
    { to: "/privacy-policy", label: t.footer.privacy, id: "footer-privacy-link" },
    { to: "/terms", label: t.footer.terms, id: "footer-terms-link" },
    { to: "/shipping", label: t.footer.shipping, id: "footer-shipping-link" },
    { to: "/refund", label: t.footer.refund, id: "footer-refund-link" },
  ];

  return (
    <footer className="border-t hairline bg-black">
      <div className="mx-auto max-w-7xl px-6 py-16 lg:px-10 lg:py-20">
        <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <Link to="/" className="flex items-center gap-2.5" data-testid="footer-brand-logo">
              <Leaf className="h-5 w-5 text-gold" strokeWidth={1.5} />
              <span className="font-serif text-2xl font-semibold tracking-[0.22em] text-cream">
                KAZIM<span className="text-gold">'S</span>
              </span>
            </Link>
            <p className="mt-5 max-w-xs font-serif text-lg italic leading-relaxed text-cream/60">
              {t.footer.tagline}
            </p>
            <a
              href={BUSINESS.instagram}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="footer-instagram-link"
              aria-label="KAZIM'S on Instagram"
              className="mt-6 inline-flex items-center gap-2 text-sm text-cream/70 transition-colors hover:text-gold"
            >
              <Instagram className="h-4 w-4" strokeWidth={1.5} />
              {BUSINESS.instagramHandle}
            </a>
          </div>

          <div>
            <h4 className="text-xs font-medium uppercase tracking-luxe text-gold">
              {t.footer.quick}
            </h4>
            <ul className="mt-6 space-y-3">
              {links.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={l.id}
                    className="text-sm text-cream/70 transition-colors hover:text-gold"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-medium uppercase tracking-luxe text-gold">
              {t.footer.contactTitle}
            </h4>
            <ul className="mt-6 space-y-3 text-sm text-cream/70">
              <li>
                <a
                  href={BUSINESS.tel}
                  data-testid="footer-phone-link"
                  className="inline-flex items-center gap-2.5 transition-colors hover:text-gold"
                >
                  <Phone className="h-4 w-4 text-gold/70" strokeWidth={1.5} />
                  {BUSINESS.phoneDisplay}
                </a>
              </li>
              <li>
                <a
                  href={BUSINESS.mailto}
                  data-testid="footer-email-link"
                  className="inline-flex items-center gap-2.5 transition-colors hover:text-gold"
                >
                  <Mail className="h-4 w-4 text-gold/70" strokeWidth={1.5} />
                  {BUSINESS.email}
                </a>
              </li>
              <li>
                <a
                  href={BUSINESS.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="footer-whatsapp-link"
                  className="inline-flex items-center gap-2.5 transition-colors hover:text-gold"
                >
                  <WhatsAppIcon className="h-4 w-4 text-gold/70" />
                  WhatsApp {BUSINESS.phoneDisplay}
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-medium uppercase tracking-luxe text-gold">
              {t.footer.addressTitle}
            </h4>
            <address className="mt-6 space-y-1 text-sm not-italic leading-relaxed text-cream/70">
              {BUSINESS.addressLines[lang].map((line) => (
                <p key={line}>{line}</p>
              ))}
            </address>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-5 border-t hairline pt-8 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-xs tracking-wide text-cream/50">{t.footer.rights}</p>
          <ul className="flex flex-wrap gap-x-6 gap-y-2">
            {legal.map((l) => (
              <li key={l.to}>
                <Link
                  to={l.to}
                  data-testid={l.id}
                  className="text-xs tracking-wide text-cream/50 transition-colors hover:text-gold"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </footer>
  );
}
